<template>
    <div>
        <ConfirmDialog
            :is-open="showConfirmDialog"
            :data="confirmDialogData"
            @on-confirm="confirmDialogConfirm"
            @on-abort="confirmDialogClose"
        >
            <template #title>
                {{ confirmDialogData.title }}
            </template>
            <template #content>
                {{ confirmDialogData.content }}
            </template>
        </ConfirmDialog>
        <CampaignSetDialog
            :is-open="showCampaignSetDialog"
            :set-data="currentSet"
            @on-save="saveCampaignSetDialog"
            @on-close="closeCampaignSetDialog"
        />
        <CampaignSetProductDialog
            :is-open="showCampaignSetProductDialog"
            :set-product-data="currentSetProduct"
            :product-list="filteredProductList(currentSet)"
            @on-close="closeCampaignSetProductDialog"
            @on-save="saveCampaignSetProductDialog"
        />
        <v-card flat>
            <v-card-text>
                <v-row>
                    <v-col
                        cols="12"
                        sm="12"
                    >
                        <v-tooltip :open-delay="tooltipDelay" top>
                            <template #activator="{on}">
                                <v-btn
                                    fab
                                    color="secondary"
                                    x-small
                                    elevation="1"
                                    class="mr-9 mt-1"
                                    data-sentry-label="Dodaj zestaw"
                                    :disabled="!isEditAllowed"
                                    v-on="on"
                                    @click="createCampaignSetDialog"
                                >
                                    <v-icon color="white">
                                        mdi-plus
                                    </v-icon>
                                </v-btn>
                            </template>
                            <span>Dodaj zestaw</span>
                        </v-tooltip>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <v-expansion-panels
                            accordion
                        >
                            <v-expansion-panel
                                v-for="set in productSets"
                                :key="set.id"
                            >
                                <v-expansion-panel-header>
                                    <v-row 
                                        no-gutters 
                                        align="center" 
                                        dense
                                        class="mt-2"
                                    >
                                        <v-col cols="12" sm="4">
                                            Nazwa: <strong>{{ set.name }}</strong>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            Kod EAN: <strong>{{ set.EAN }}</strong>
                                        </v-col>
                                        <v-col cols="12" sm="3">
                                            Index: <strong>{{ set.index }}</strong>
                                        </v-col>
                                        <v-col cols="12" sm="2">
                                            <v-container fluid class="pa-0">
                                                <v-row justify="end" dense>
                                                    <v-tooltip
                                                        :open-delay="tooltipDelay"
                                                        top
                                                    >
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                icon
                                                                data-sentry-label="Rozdzielnik kompletu"
                                                                v-on="on"
                                                                @click="generateCampaignProductSetReport(set.id)"
                                                            >
                                                                <v-icon color="blue">
                                                                    mdi-printer
                                                                </v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Rozdzielnik kompletu</span>
                                                    </v-tooltip>
                                                    <v-tooltip
                                                        :open-delay="tooltipDelay"
                                                        top
                                                    >
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                icon
                                                                data-sentry-label="Edycja zestawu"
                                                                :disabled="!isEditAllowed"
                                                                v-on="on"
                                                                @click="openEditCampaignSetDialog(set)"
                                                            >
                                                                <v-icon color="teal">
                                                                    mdi-pencil
                                                                </v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Edycja zestawu</span>
                                                    </v-tooltip>
                                                    <v-tooltip
                                                        :open-delay="tooltipDelay"
                                                        top
                                                    >
                                                        <template #activator="{on}">
                                                            <v-btn
                                                                icon
                                                                data-sentry-label="Usuń zestaw"
                                                                :disabled="!isEditAllowed"
                                                                v-on="on"
                                                                @click="tryDeleteSet(set)"
                                                            >
                                                                <v-icon color="pink">
                                                                    mdi-delete
                                                                </v-icon>
                                                            </v-btn>
                                                        </template>
                                                        <span>Usuń zestaw</span>
                                                    </v-tooltip>
                                                </v-row>
                                            </v-container>
                                        </v-col>
                                    </v-row>
                                </v-expansion-panel-header>

                                <v-divider />

                                <!--  Produkty -->
                                <v-expansion-panel-content>
                                    <v-row>
                                        <v-col
                                            cols="12"
                                            sm="12"
                                        >
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        fab
                                                        color="secondary"
                                                        x-small
                                                        elevation="1"
                                                        class="mr-9 mt-1"
                                                        data-sentry-label="Dodaj"
                                                        :disabled="filteredProductList(set).length === 0 || !isEditAllowed"
                                                        v-on="on"
                                                        @click="createCampaignSetProductDialog(set)"
                                                    >
                                                        <v-icon color="white">
                                                            mdi-plus
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Dodaj produkt</span>
                                            </v-tooltip>
                                        </v-col>
                                    </v-row>
                                    <v-data-table
                                        :headers="setsHeaders"
                                        :items="set.products"
                                        dense
                                        :loading="loading"
                                        item-key="id"
                                        :mobile-breakpoint="0"
                                        no-data-text="Brak danych"
                                        :items-per-page="17"
                                        hide-default-footer
                                        class="elevation-1"
                                    >
                                        <template #item.amount="{item}">
                                            {{ item.amount }}
                                        </template>
                                        <template #item.product.name="{item}">
                                            {{ item.product.name }}
                                        </template>
                                        <template #item.actions="{item}">
                                            <div justify="end" class="pa-2 text-end">
                                                <v-tooltip :open-delay="tooltipDelay" top>
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Edytuj"
                                                            :disabled="!isEditAllowed"
                                                            v-on="on"
                                                            @click.stop="openEditCampaignSetProductDialog(set, item)"
                                                        >
                                                            <v-icon color="teal">
                                                                mdi-pencil
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Edytuj</span>
                                                </v-tooltip>
                                                <v-tooltip
                                                    :open-delay="tooltipDelay"
                                                    top
                                                >
                                                    <template #activator="{on}">
                                                        <v-btn
                                                            icon
                                                            data-sentry-label="Usuń"
                                                            :disabled="!isEditAllowed"
                                                            v-on="on"
                                                            @click.stop="tryDeleteSetProduct(set, item)"
                                                        >
                                                            <v-icon color="pink">
                                                                mdi-delete
                                                            </v-icon>
                                                        </v-btn>
                                                    </template>
                                                    <span>Usuń</span>
                                                </v-tooltip>
                                            </div>
                                        </template>
                                    </v-data-table>
                                </v-expansion-panel-content>
                                <!-- /Produkty -->
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-col>
                </v-row>
            </v-card-text>
        </v-card>
    </div>
</template>

<script>
import { errorHandler } from '@/utils/errorHandler'
import CampaignSetDialog from '@/views/Campaigns/Dialogs/CampaignSetDialog'
import CampaignSetProductDialog from '@/views/Campaigns/Dialogs/CampaignSetProductDialog'
import ConfirmDialog from '@/components/common/ConfirmDialog'

export default {
    name: 'SetList',
    components: {
        CampaignSetDialog,
        CampaignSetProductDialog,
        ConfirmDialog
    },
    props: {
        productSets: {
            type: Array,
            required: true,
        },
        campaignId: {
            type: Number,
            required: true,
        },
        productList: {
            type: Array,
            required: true,
        },
        isEditAllowed: {
            type: Boolean,
            required: true,
        },
        loading: {
            type: Boolean,
        },
    },
    data() {
        return {
            currentSet: null,
            currentSetProduct: null,
            setsHeaders: [
                {
                    text: 'Nazwa',
                    value: 'product.name',
                },
                {
                    text: 'Ilość',
                    value: 'amount',
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 120,

                },
            ],
            tooltipDelay: 500,
            showCampaignSetDialog: false,
            showCampaignSetProductDialog: false,
            showConfirmDialog: false,
            confirmDialogData: {
                confirm: () => {},
            },
        }
    },
    computed: {
        userEmail() {
            return this.$store.state.user.userEmail
        },
    },
    methods: {
        createCampaignSetDialog() {
            this.currentSet = null
            this.showCampaignSetDialog = true
        },
        generateCampaignProductSetReport(productSetId) {
            this.$store.dispatch('campaigns/generateProductSetReport', {
                productSetId,
                userEmail: this.userEmail
            })
                .catch(error => {
                    errorHandler(error)
                })
        },
        createCampaignSetProductDialog(set) {
            this.currentSet = set
            this.currentSetProduct = null
            this.showCampaignSetProductDialog = true
        },
        openEditCampaignSetDialog(set) {
            this.currentSet = set
            this.showCampaignSetDialog = true
        },
        openEditCampaignSetProductDialog(set, setProduct) {
            this.currentSet = set
            this.currentSetProduct = setProduct
            this.showCampaignSetProductDialog = true
        },
        saveCampaignSetDialog(set) {
            if (this.currentSet) {
                this.updateSet(set)
            } else {
                this.createSet(set)
            }
        },
        filteredProductList(set) {
            if (!set) {
                return this.productList
            }

            return this.productList.filter(product => {
                return !set.products.some(setProduct => setProduct.productId === product.id)
            })
        },
        saveCampaignSetProductDialog(setProduct) {
            let updatedSet = Object.assign({}, this.currentSet)

            if (this.currentSetProduct) {
                let index = updatedSet.products.findIndex(product => product.id === setProduct.id)

                if (index != -1 ) {
                    updatedSet.products[index] = setProduct
                }
            } else {
                updatedSet.products.push(setProduct)
            }

            this.updateSet(updatedSet)
        },
        deleteCampaignProduct({ data }) {
            let updatedSet = Object.assign({}, data.set)

            let index = updatedSet.products.findIndex(product => product.id === data.setProduct.id)

            if (index != -1 ) {
                updatedSet.products.splice(index, 1)
            }

            this.updateSet(updatedSet)
        },
        updateSet(set) {
            this.$store.dispatch('campaigns/updateCampaignSet', {
                ...this.currentSet,
                ...set
            })
                .then(() => {
                    this.$emit('set-changed', () => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Zestaw kampanii został zaktualizowany.'
                        })
                    })
                })
                .finally(() => {
                    this.closeCampaignSetDialog()
                    this.closeCampaignSetProductDialog()

                })
        },
        createSet(set) {
            this.$store.dispatch('campaigns/createCampaignSet', {
                ...set,
                campaignId: this.campaignId,
            })
                .then(() => {
                    this.$emit('set-changed', () => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Zestaw kampanii został dodany.'
                        })
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeCampaignSetDialog()
                })
        },
        deleteCampaignSet({ data }) {
            this.$store.dispatch('campaigns/deleteCampaignSet', data.id)
                .then(() => {
                    this.$emit('set-changed', () => {
                        this.$store.dispatch('showNotification', {
                            type: 'success',
                            message: 'Zestaw kampanii został usunięty.'
                        })
                    })
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        closeCampaignSetDialog() {
            this.currentSet = null
            this.showCampaignSetDialog = false
        },
        closeCampaignSetProductDialog() {
            this.currentSet = null
            this.currentSetProduct = null
            this.showCampaignSetProductDialog = false
        },
        tryDeleteSet(set) {
            this.confirmDialogData = {
                title: 'Usuwanie zestawu',
                content: 'Czy na pewno chcesz usunąć zestaw?',
                confirm: this.deleteCampaignSet,
                data: set,
            }
            this.showConfirmDialog = true
        },
        tryDeleteSetProduct(set, setProduct) {
            this.confirmDialogData = {
                title: 'Usuwanie produktu',
                content: 'Czy na pewno chcesz usunąć produkt?',
                confirm: this.deleteCampaignProduct,
                data: { set, setProduct }
            }
            this.showConfirmDialog = true
        },
        confirmDialogConfirm(payload) {
            this.confirmDialogData.confirm(payload)
            this.confirmDialogClose()
        },
        confirmDialogClose() {
            this.showConfirmDialog = false
            setTimeout(() => {
                this.confirmDialogData = {
                    title: null,
                    content: null,
                    confirm: () => {},
                    data: null,
                }
            }, 500)
        },
    },
}
</script>